import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { VOLUME_IN_OPTION_VALUES } from 'constants/businessTool';

import { AutocompleteControl } from 'components/_basic/Form/Autocomplete';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { BorderedCard } from 'components/BorderedCard';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';
import { adornmentIconMap } from 'components/BusinessToolForm/components/VolumeInputField/constants';
import { PREMISE_TERMS_OF_USE_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { PREMISE_TERMS_OF_USE_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { PREMISE_TERMS_OF_USE_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { PREMISE_TERMS_OF_USE_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { concatWithDot } from 'helpers/string';

import { PREMISES_INFORMATION_NAMES } from '../utils/form';

export const PremisesInformation: FC<IProps> = memo(({ name, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();

  const getChosenOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return PREMISE_TERMS_OF_USE_OPTIONS_DEU;

      case 'uk':
        return PREMISE_TERMS_OF_USE_OPTIONS_UA;

      case 'es':
        return PREMISE_TERMS_OF_USE_OPTIONS_ES;

      default:
        return PREMISE_TERMS_OF_USE_OPTIONS;
    }
  };

  return (
    <BorderedCard>
      <Grid container spacing={3}>
        <Grid item xs="auto">
          <TooltipFormImageUpload
            width={136}
            height={136}
            name={concatWithDot(name, PREMISES_INFORMATION_NAMES.FILE)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, PREMISES_INFORMATION_NAMES.ADDRESS)}
                name={concatWithDot(name, PREMISES_INFORMATION_NAMES.ADDRESS)}
                label={t('businessOperations.location.detailedInformation.address')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(name, PREMISES_INFORMATION_NAMES.AREA)}
                name={concatWithDot(name, PREMISES_INFORMATION_NAMES.AREA)}
                label={t('businessOperations.location.detailedInformation.area')}
                endAdornmentIcon={adornmentIconMap[VOLUME_IN_OPTION_VALUES.SQUARE_METER.value]}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <AutocompleteControl
                control={control}
                id={concatWithDot(name, PREMISES_INFORMATION_NAMES.TERMS_OF_USE)}
                name={concatWithDot(name, PREMISES_INFORMATION_NAMES.TERMS_OF_USE)}
                label={t('businessOperations.location.detailedInformation.termsOfUse')}
                options={getChosenOptions(i18n.language)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberInputControlled
                control={control}
                id={concatWithDot(name, PREMISES_INFORMATION_NAMES.CURRENT_COST)}
                name={concatWithDot(name, PREMISES_INFORMATION_NAMES.CURRENT_COST)}
                label={t('businessOperations.location.detailedInformation.currentCost')}
                adornmentType="currency"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                multiline
                control={control}
                id={concatWithDot(name, PREMISES_INFORMATION_NAMES.PURPOSE)}
                name={concatWithDot(name, PREMISES_INFORMATION_NAMES.PURPOSE)}
                label={t('businessOperations.location.detailedInformation.purpose')}
                minRows={4}
                disabled={disabled}
                maxLength={500}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
